import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Button, Header, Pagination, PropertyFilter, SpaceBetween } from "@amzn/awsui-components-react";
import { Table } from "@amzn/awsui-components-react/polaris";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { EmptyState, getMatchesCountText, PROPERTY_FILTERING_I18N_CONSTANTS } from "../../../phastos/table-config";
import { getReportingAggregatedDataForDST, getAllReportingAggregatedDataForDST } from "../redux/exceptionAPIs";
import { isNull, isUndefined } from "lodash";
import { convertStringToFloatWithPrecision, convertStringToPercentage, downloadJsonAsExcel } from "../../utils/utils";
import DSTAdjustmentModal from "./DSTAdjustmentModal";
import BulkAdjustmentModal from "../common/adjustment/BulkAdjustmentModal";
import { BULK_ADJUSTMENT_USE_CASE } from "../../../../common/constants/constants";
import { getCurrencyAmountForSummary } from "../common/adjustment/constants/errorSummaryUtils";

// Constants moved outside component
const DST_COLUMN_IDENTIFIER = {
    Month: 'month',
    LegalEntity: 'legalEntity',
    MarketplaceId: 'marketplaceId',
    SubBusiness: "subBusiness",
    TotalDSTScopeTransaction: 'totalDSTScopeTransaction',
    TotalTransaction: 'totalTransaction',
    NexusRatio: "nexusRatio",
    DSTRate: "dstRate",
    TotalRevenue: "totalRevenue",
    DSTBaseAmount: "dstBaseAmount",
    DSTLiability: "dstLiability",
    TotalRevenueDstReportingCurrency: "totalRevenueAmountDstReportingCurrency",
    TotalDstBaseAmountDstReportingCurrency: "dstBaseAmountDstReportingCurrency",
    TotalDstLiabilityDstReportingCurrency: "dstLiabilityDstReportingCurrency",
    AdjustmentDescription: "adjustmentDescription",
    UpdatedBy: "updatedBy",
    UpdatedOn: "updatedOn",
    Category: "category",
    DocumentLink: 'link',
};

const INITIAL_PAGE_NUMBER = 1;

// Empty states moved outside component
const EMPTY_STATES = {
    noSummary: (
        <EmptyState
            title="No summary"
            subtitle="No summary to display."
        />
    ),
    noMatches: (
        <EmptyState
            title="No matches"
            subtitle="We can't find a match."
        />
    )
};

// Filtering properties moved outside component
const FILTERING_PROPERTIES = [
    {
        propertyLabel: 'Month',
        key: 'month',
        groupValuesLabel: 'Month',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Legal Entity',
        key: 'legalEntity',
        groupValuesLabel: 'Legal Entity',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Category',
        key: 'category',
        groupValuesLabel: 'Category',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Sub Business',
        key: 'subBusiness',
        groupValuesLabel: 'subBusiness',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Market Place',
        key: 'marketplaceId',
        groupValuesLabel: 'marketplaceId',
        operators: ['=', '!='],
    }
];

function EntityBreakdown(props) {
    const {
        parentWorkflowId,
        useCase,
        lastUpdatedOn,
        setLastUpdatedOn,
        entityFilter,
        dstDispatcher
    } = props;

    // State management
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(INITIAL_PAGE_NUMBER);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [openEnd, setOpenEnd] = useState(true);
    const [maxPageReached, setMaxPageReached] = useState(1);
    const [selectedItem, setSelectedItem] = useState();
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [showBulkAdjustmentModal, setShowBulkAdjustmentModal] = useState(false);
    const [disableAdjustment, setDisableAdjustment] = useState(true);
    const [disableBulkAdjustment, setDisableBulkAdjustment] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Ref to track initial load
    const hasInitiallyLoaded = useRef(false);

    // Constants
    const FIXED_PRECISION = 2;
    const DST_RATE_FIXED_PRECISION = 3;

    const getPageData = useCallback((pageNumber, dataObj = data) => {
        if (!dataObj || !dataObj[pageNumber]) {
            return [];
        }
        const pageData = dataObj[pageNumber];
        return Array.isArray(pageData) ? pageData : Object.values(pageData);
    }, [data]);

    const resetPagination = () => {
        setCurrentPageNumber(INITIAL_PAGE_NUMBER);
        setMaxPageReached(1);
        setLastEvaluatedKey(null);
        setOpenEnd(true);
    };

    const refreshData = useCallback((pageNumber, isRefresh = false) => {
        if (loading) return;
        
        setLoading(true);
        const dataToUse = isRefresh ? {} : data;
        const keyToUse = isRefresh ? null : lastEvaluatedKey;
        
        const request = {
            data: dataToUse,
            setData,
            setLastUpdatedOn,
            lastEvaluatedKey: keyToUse,
            setLastEvaluatedKey,
            parentWorkflowId,
            useCase,
            currentPageNumber: pageNumber || currentPageNumber,
            setOpenEnd,
            setLoading
        };
        
        try {
            getReportingAggregatedDataForDST(request);
        } catch (error) {
            console.error('Error fetching DST data:', error);
            setLoading(false);
        }
    }, [loading, data, lastEvaluatedKey, parentWorkflowId, useCase, currentPageNumber, setLastUpdatedOn]);

    const onPaginationChange = (event) => {
        const pageNumber = event.detail.currentPageIndex;
        
        if (pageNumber < 1) return;
        if (!openEnd && pageNumber > maxPageReached) return;
        
        setCurrentPageNumber(pageNumber);

        if (isNull(data[pageNumber]) || isUndefined(data[pageNumber])) {
            refreshData(pageNumber);
        } else {
            setCurrentPageData(getPageData(pageNumber, data));
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        resetPagination();
        setData({});
        setCurrentPageData([]);
        hasInitiallyLoaded.current = false;
        refreshData(INITIAL_PAGE_NUMBER, true);
    };

    const adjustmentModalDismissHandler = () => {
        setShowAdjustmentModal(false);
    };

    const bulkAdjustmentModalDismissHandler = () => {
        setShowBulkAdjustmentModal(false);
    };

    const onAdjustmentCloseHandler = useCallback(async () => {
        refreshData();
        setDisableAdjustment(true);
    }, [refreshData]);

    const onBulkAdjustmentCloseHandler = useCallback(async () => {
        refreshData();
        setDisableBulkAdjustment(false);
    }, [refreshData]);

    function getFlatEntityMarketPlaceData(dataList) {
        const updatedDataList = [];
        for (let i = 0; i < dataList.length; i++) {
            let val = JSON.parse(dataList[i]); // Parse the JSON string from API response
            let cur = {
                "Month": val?.month,
                "Category": val?.businessCategory,
                "Legal Entity": val?.legalEntity,
                "Legal Entity Short Name": val?.legalEntityShortName,
                "Marketplace Id": val?.marketplaceId,
                "Sub Business": val?.subBusiness,
                "Total Transaction": val?.totalTransaction,
                "Total DST Scope Transaction": val?.totalDSTScopeTransaction,
                "Nexus Ratio": convertStringToPercentage(val?.nexusRatio),
                "DST Rate": convertStringToFloatWithPrecision(val?.dstRate, DST_RATE_FIXED_PRECISION),
                "Total Revenue (Local Currency)": val?.totalRevenue,
                "Total Revenue (DST Reporting Currency)": convertStringToFloatWithPrecision(val?.totalRevenueAmountDstReportingCurrency, FIXED_PRECISION),
                "Total DST Base Amount (DST Reporting Currency)": convertStringToFloatWithPrecision(val?.dstBaseAmountDstReportingCurrency, FIXED_PRECISION),
                "Total DST Liability (DST Reporting Currency)": convertStringToFloatWithPrecision(val?.dstLiabilityDstReportingCurrency, FIXED_PRECISION),
                "Local Currency": val?.localCurrency,
                "DST Reporting Currency": val?.dstReportingCurrency,
            };
            updatedDataList.push(cur);
        }
        return updatedDataList;
    }

    const handleDownloadAllData = async () => {
        setDownloadLoading(true);
        try {
            const request = {
                parentWorkflowId,
                useCase
            };

            const allData = await getAllReportingAggregatedDataForDST(request);
            const flatData = getFlatEntityMarketPlaceData(allData);
            downloadJsonAsExcel(flatData, "EntityBreakdown_Complete");
        } catch (error) {
            // Fallback to current data if API fails
            const currentDataList = Object.values(data).flatMap(page =>
                Array.isArray(page) ? page : Object.values(page)
            );
            const currentFlatData = getFlatEntityMarketPlaceData(currentDataList.map(item => JSON.stringify(item)));
            downloadJsonAsExcel(currentFlatData, "EntityBreakdown_Current");
        } finally {
            setDownloadLoading(false);
        }
    };

    // Memoized column definitions with sorting
    const columnDefinitions = useMemo(() => [
        {
            id: DST_COLUMN_IDENTIFIER.Month,
            cell: item => item.month,
            header: 'Month',
            sortingField: 'month',
            sortingComparator: (a, b) => {
                const parseMonth = (monthStr) => {
                    if (!monthStr) return new Date(0);
                    const [monthName, year] = monthStr.split('_');
                    const monthMap = {
                        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
                        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
                    };
                    const monthIndex = monthMap[monthName];
                    const yearNum = parseInt(year, 10);
                    return new Date(yearNum, monthIndex, 1);
                };
                const dateA = parseMonth(a.month);
                const dateB = parseMonth(b.month);
                return dateA.getTime() - dateB.getTime();
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.Category,
            cell: item => item.businessCategory,
            header: 'Category',
            sortingField: 'businessCategory'
        },
        {
            id: DST_COLUMN_IDENTIFIER.LegalEntity,
            cell: item => item.legalEntity,
            header: 'Legal Entity',
            sortingField: 'legalEntity'
        },
        {
            id: DST_COLUMN_IDENTIFIER.MarketplaceId,
            cell: item => item.marketplaceId,
            header: 'Marketplace Id',
            sortingField: 'marketplaceId'
        },
        {
            id: DST_COLUMN_IDENTIFIER.SubBusiness,
            cell: item => item.subBusiness,
            header: 'Sub Business',
            sortingField: 'subBusiness'
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalDSTScopeTransaction,
            cell: item => getCurrencyAmountForSummary(item.totalDSTScopeTransaction),
            header: 'Total Transaction DST Scope',
            sortingField: 'totalDSTScopeTransaction',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.totalDSTScopeTransaction || 0);
                const valueB = parseFloat(b.totalDSTScopeTransaction || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalTransaction,
            cell: item => getCurrencyAmountForSummary(item.totalTransaction),
            header: 'Total Transaction',
            sortingField: 'totalTransaction',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.totalTransaction || 0);
                const valueB = parseFloat(b.totalTransaction || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.NexusRatio,
            cell: item => convertStringToPercentage(item.nexusRatio),
            header: 'Nexus Ratio',
            sortingField: 'nexusRatio',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.nexusRatio || 0);
                const valueB = parseFloat(b.nexusRatio || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.DSTRate,
            cell: item => convertStringToPercentage(item.dstRate),
            header: 'DST Rate',
            sortingField: 'dstRate',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstRate || 0);
                const valueB = parseFloat(b.dstRate || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalRevenue,
            cell: item => getCurrencyAmountForSummary(item.totalRevenue, item.localCurrency),
            header: 'Total Revenue (Local Currency)',
            sortingField: 'totalRevenue',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.totalRevenue || 0);
                const valueB = parseFloat(b.totalRevenue || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.DSTBaseAmount,
            cell: item => getCurrencyAmountForSummary(item.dstBaseAmount, item.localCurrency),
            header: 'DST Base Revenue (Local Currency)',
            sortingField: 'dstBaseAmount',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstBaseAmount || 0);
                const valueB = parseFloat(b.dstBaseAmount || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.DSTLiability,
            cell: item => getCurrencyAmountForSummary(item.dstLiability, item.localCurrency),
            header: 'DST Liability (Local Currency)',
            sortingField: 'dstLiability',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstLiability || 0);
                const valueB = parseFloat(b.dstLiability || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalRevenueDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.totalRevenueAmountDstReportingCurrency, item.dstReportingCurrency),
            header: 'Total Revenue (DST Reporting Currency)',
            sortingField: 'totalRevenueAmountDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.totalRevenueAmountDstReportingCurrency || 0);
                const valueB = parseFloat(b.totalRevenueAmountDstReportingCurrency || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalDstBaseAmountDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.dstBaseAmountDstReportingCurrency, item.dstReportingCurrency),
            header: 'DST Base Revenue (DST Reporting Currency)',
            sortingField: 'dstBaseAmountDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstBaseAmountDstReportingCurrency || 0);
                const valueB = parseFloat(b.dstBaseAmountDstReportingCurrency || 0);
                return valueA - valueB;
            }
        },
        {
            id: DST_COLUMN_IDENTIFIER.TotalDstLiabilityDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.dstLiabilityDstReportingCurrency, item.dstReportingCurrency),
            header: 'DST Liability (DST Reporting Currency)',
            sortingField: 'dstLiabilityDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstLiabilityDstReportingCurrency || 0);
                const valueB = parseFloat(b.dstLiabilityDstReportingCurrency || 0);
                return valueA - valueB;
            }
        }
    ], []);

    // Effects
    useEffect(() => {
        if (!hasInitiallyLoaded.current) {
            hasInitiallyLoaded.current = true;
            refreshData(INITIAL_PAGE_NUMBER, true);
        }
    }, [refreshData]);

    useEffect(() => {
        if (!isNull(data[currentPageNumber]) && !isUndefined(data[currentPageNumber])) {
            setCurrentPageData(getPageData(currentPageNumber, data));
            setLoading(false);
            setMaxPageReached(prev => Math.max(prev, currentPageNumber));
        }
    }, [data, currentPageNumber, getPageData]);

    // Memoized collection configuration
    const collectionConfig = useMemo(() => ({
        filtering: {
            empty: EMPTY_STATES.noSummary,
            noMatch: EMPTY_STATES.noMatches,
        },
        propertyFiltering: {
            filteringProperties: FILTERING_PROPERTIES,
            empty: <EmptyState title="No matches"/>,
            noMatch: EMPTY_STATES.noMatches,
        },
        sorting: {
            defaultState: {
                sortingColumn: {
                    sortingField: 'month'
                },
                isDescending: true
            }
        },
        selection: {}
    }), []);

    const { items, filteredItemsCount, collectionProps, propertyFilterProps, actions: filterAction } = useCollection(
        currentPageData,
        collectionConfig
    );

    const { setPropertyFiltering } = filterAction;

    useEffect(() => {
        if (!isNull(entityFilter)) {
            setPropertyFiltering({
                tokens: [{
                    propertyKey: 'legalEntity',
                    operator: '=',
                    value: entityFilter,
                }],
                operation: 'and'
            });
        } else {
            setPropertyFiltering({
                tokens: [],
                operation: 'and'
            });
        }
    }, [entityFilter, setPropertyFiltering]);

    return (
        <SpaceBetween size="xl">
            <Table
                {...collectionProps}
                loading={loading}
                sortingDisabled={false}
                selectionType="single"
                selectedItems={selectedItem}
                onSelectionChange={({ detail }) => {
                    setSelectedItem(detail.selectedItems);
                    setDisableAdjustment(false);
                }}
                header={
                    <div>
                        <Header
                            actions={<SpaceBetween size='xs' direction='horizontal'>
                                <Button onClick={handleRefresh}>Refresh</Button>
                                <Button
                                    onClick={handleDownloadAllData}
                                    loading={downloadLoading}
                                    disabled={downloadLoading}
                                >
                                    {downloadLoading ? 'Downloading...' : 'Download All Data'}
                                </Button>
                                <Button disabled={disableAdjustment} onClick={() => {
                                    setShowAdjustmentModal(true);
                                }}>Apply adjustments</Button>
                                <Button disabled={disableBulkAdjustment} onClick={() => {
                                    setShowBulkAdjustmentModal(true);
                                }}>Apply Bulk adjustments</Button>
                            </SpaceBetween>}
                            description={`Last updated: ${lastUpdatedOn}`}
                        >
                            Entity Breakdown
                        </Header>
                    </div>
                }
                columnDefinitions={columnDefinitions}
                resizableColumns={false}
                items={items}
                filter={
                    <PropertyFilter
                        i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                        filteringProperties={propertyFilterProps.filteringProperties}
                        filteringOptions={propertyFilterProps.filteringOptions}
                        onChange={propertyFilterProps.onChange}
                        query={propertyFilterProps.query}
                        countText={getMatchesCountText(filteredItemsCount)}
                    />
                }
                pagination={<Pagination
                    currentPageIndex={currentPageNumber}
                    onChange={onPaginationChange}
                    pagesCount={openEnd ? undefined : maxPageReached}
                    openEnd={openEnd}
                    disabled={loading}
                />}
            />
            {showAdjustmentModal ? <DSTAdjustmentModal
                showAdjustmentModal={showAdjustmentModal}
                adjustmentModalDismissHandler={adjustmentModalDismissHandler}
                dstItem={selectedItem[0]}
                useCase={useCase}
                parentWorkflowId={parentWorkflowId}
                onAdjustmentCloseHandler={onAdjustmentCloseHandler}
                dstDispatcher={dstDispatcher}
            /> : <></>}
            {showBulkAdjustmentModal ? <BulkAdjustmentModal
                bulkAdjustmentUseCase={BULK_ADJUSTMENT_USE_CASE.DST}
                showBulkAdjustmentModal={showBulkAdjustmentModal}
                useCase={useCase}
                parentWorkflowId={parentWorkflowId}
                bulkAdjustmentModalDismissHandler={bulkAdjustmentModalDismissHandler}
                onBulkAdjustmentCloseHandler={onBulkAdjustmentCloseHandler}
                dispatcher={dstDispatcher}
            /> : <></>}
        </SpaceBetween>
    );
}

export default EntityBreakdown;
